/**
 * Shared media uploader (Feb 2026, Track B Slice 1).
 *
 * Drag-drop OR tap-to-upload OR camera-capture (mobile) → POST to
 * /api/uploads/media → returns {id, url, kind}. Consumers pass an
 * onUploaded callback with the returned metadata and store the ids in
 * their own doc (reviews, chat messages, etc.).
 *
 * Client-side compression: images >2 MB get scaled down to a max edge of
 * 1600 px + JPEG re-encoded at quality 0.85 before upload. That keeps the
 * request small on mobile data without shipping a heavy image lib.
 *
 * Videos: passed through as-is (browser can't easily transcode). Size
 * limit is enforced server-side (50 MB).
 */
import React, { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import { Camera, Upload, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif,image/gif';
const VIDEO_ACCEPT = 'video/mp4,video/quicktime,video/webm';
const IMAGE_COMPRESS_THRESHOLD = 2 * 1024 * 1024; // 2 MB
const IMAGE_MAX_EDGE = 1600;
const CODE_BTN = "flex items-center gap-1.5 border border-[#2A0F3D] text-[#39FF14] font-mono text-xs uppercase tracking-wider px-3 py-2 hover:border-[#39FF14] disabled:opacity-40 disabled:cursor-not-allowed";
const MODERN_BTN = 'flex items-center gap-1.5 rounded-full border border-[#0F1712]/25 bg-white text-[#0F1712] text-sm font-medium px-4 py-2 hover:bg-[#0F1712]/5 disabled:opacity-50 disabled:cursor-not-allowed';

async function compressImageIfNeeded(file) {
  if (!file.type.startsWith('image/')) return file;
  if (file.type === 'image/gif') return file; // preserve animations
  if (file.size <= IMAGE_COMPRESS_THRESHOLD) return file;

  return new Promise((resolve) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => { img.src = e.target.result; };
    img.onload = () => {
      const scale = Math.min(1, IMAGE_MAX_EDGE / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => {
          if (!blob) { resolve(file); return; }
          resolve(new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' }));
        },
        'image/jpeg',
        0.85,
      );
    };
    img.onerror = () => resolve(file);
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
}

/**
 * @param {(file: {id,url,thumb_url,kind,content_type,size}) => void} onUploaded
 * @param {'image' | 'video' | 'any'} accept — restrict input type
 * @param {number} maxCount — max concurrent uploads before disabling the button
 * @param {number} currentCount — how many files the parent already has attached
 * @param {string} [label] — custom button label
 * @param {string} [testIdPrefix='media-uploader'] — for data-testid
 */
export default function MediaUploader({
  onUploaded,
  accept = 'any',
  maxCount = 5,
  currentCount = 0,
  label,
  testIdPrefix = 'media-uploader',
  modern = false,
}) {
  const btnCls = modern ? MODERN_BTN : CODE_BTN;
  const slotCls = modern ? 'text-xs text-[#5B6B60]' : 'text-[10px] font-mono text-[#4B6B52]';
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);
  const cameraRef = useRef(null);

  const disabled = uploading || currentCount >= maxCount;
  const remaining = Math.max(0, maxCount - currentCount);
  const acceptAttr =
    accept === 'image' ? IMAGE_ACCEPT
    : accept === 'video' ? VIDEO_ACCEPT
    : `${IMAGE_ACCEPT},${VIDEO_ACCEPT}`;

  const upload = useCallback(async (rawFile) => {
    if (!rawFile) return;
    setUploading(true);
    setProgress(0);
    try {
      const file = await compressImageIfNeeded(rawFile);
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(`${API_URL}/api/uploads/media`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      onUploaded?.(data);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Upload failed';
      toast.error(detail);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }, [onUploaded]);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
    e.target.value = ''; // allow re-upload of same file
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testIdPrefix}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        className={btnCls}
        data-testid={`${testIdPrefix}-pick`}
      >
        {uploading ? (
          <>
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            <span>{progress}%</span>
          </>
        ) : (
          <>
            <Upload className="w-3.5 h-3.5" />
            <span>{label || 'Add media'}</span>
          </>
        )}
      </button>

      {/* Mobile camera shortcut — inputs with `capture` open the camera app */}
      {accept !== 'video' && (
        <button
          type="button"
          disabled={disabled}
          onClick={() => cameraRef.current?.click()}
          className={btnCls}
          data-testid={`${testIdPrefix}-camera`}
          aria-label="Take a photo"
        >
          <Camera className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Camera</span>
        </button>
      )}

      <span className={slotCls}>
        {remaining}/{maxCount} slots
      </span>

      <input
        ref={inputRef}
        type="file"
        accept={acceptAttr}
        onChange={onPick}
        className="hidden"
        data-testid={`${testIdPrefix}-input`}
      />
      <input
        ref={cameraRef}
        type="file"
        accept={IMAGE_ACCEPT}
        capture="environment"
        onChange={onPick}
        className="hidden"
        data-testid={`${testIdPrefix}-camera-input`}
      />
    </div>
  );
}

/**
 * Companion component: renders a horizontal row of uploaded-media previews
 * with a small X button on each for removal. Consumers pass the array of
 * `{id, url, kind, content_type}` items and a remove callback.
 */
export function MediaPreviewRow({ items = [], onRemove, testIdPrefix = 'media-preview' }) {
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid={testIdPrefix}>
      {items.map((it) => (
        <div key={it.id} className="relative group" data-testid={`${testIdPrefix}-item-${it.id}`}>
          {it.kind === 'video' ? (
            <video
              src={`${API_URL}${it.url}`}
              muted
              className="w-20 h-20 object-cover border border-[#2A0F3D]"
              preload="metadata"
            />
          ) : (
            <img
              src={`${API_URL}${it.url}`}
              alt="upload"
              className="w-20 h-20 object-cover border border-[#2A0F3D]"
              loading="lazy"
            />
          )}
          {onRemove && (
            <button
              type="button"
              onClick={() => onRemove(it.id)}
              className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#FF3B30] text-white flex items-center justify-center opacity-90 hover:opacity-100"
              aria-label="Remove"
              data-testid={`${testIdPrefix}-remove-${it.id}`}
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
